import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createCrmDisposition,
  createCrmSmartfloAgent,
  createCrmVariable,
  createCrmView,
  deleteCrmSmartfloAgent,
  deactivateCrmDisposition,
  deactivateCrmVariable,
  deactivateCrmView,
  getCrmSettingsDispositions,
  getCrmSettingsHubNumbers,
  getCrmSettingsSmartfloCallerIds,
  getCrmSettingsSmartfloAgents,
  getCrmSettingsSmartfloRoles,
  getCrmSettingsVariables,
  getCrmSettingsViews,
  saveCrmHubNumbers,
  searchCrmAssignableStaff,
  updateCrmDisposition,
  updateCrmVariable,
  updateCrmView,
} from '../config/api';
import { useAuth } from '../context/AuthContext';

const TABS = [
  ['views', 'Views'],
  ['variables', 'Variables'],
  ['dispositions', 'Dispositions'],
];

const ROLE_OPTIONS = [
  ['user', 'CRM staff'],
  ['manager', 'Manager'],
  ['admin', 'Admin'],
];

const STAGE_OPTIONS = [
  ['not_contacted', 'Not contacted'],
  ['contacted', 'Contacted'],
  ['interested', 'Interested'],
  ['app_registered', 'App registered'],
  ['active', 'Active'],
  ['inactive', 'Inactive'],
];

const VIEW_FILTER_FIELDS = [
  { key: 'search', label: 'Search', type: 'text', placeholder: 'Name, phone or vendor ID' },
  { key: 'stage', label: 'Stage', type: 'multi-select', options: STAGE_OPTIONS },
  { key: 'created_from', label: 'Source', type: 'select', options: [['pmsg', 'PMSG'], ['manual', 'Manual'], ['oms_signup', 'Signup']] },
  { key: 'tags', label: 'Tags', type: 'list', placeholder: 'Comma-separated tags' },
  { key: 'cart_active_no_order', label: 'Active cart, no recent order', type: 'boolean' },
  { key: 'order_presence', label: 'Order availability', type: 'select', options: [['has_orders', 'Has orders'], ['zero_orders', 'Zero orders']] },
  { key: 'follow_up_status', label: 'Follow-up status', type: 'select', options: [['overdue', 'Overdue']] },
  { key: 'assigned_user_id', label: 'Assigned user', type: 'staff' },
  { key: 'pmsg_state', label: 'PMSG state', type: 'text' },
  { key: 'pmsg_district', label: 'PMSG district', type: 'text' },
  { key: 'pmsg_address', label: 'PMSG address', type: 'text' },
  { key: 'hub_id', label: 'Hub', type: 'hub' },
  { key: 'serviceable', label: 'Serviceable', type: 'select', options: [['true', 'Yes'], ['false', 'No']] },
  { key: 'distance_min', label: 'Minimum distance (km)', type: 'number', step: '0.1' },
  { key: 'distance_max', label: 'Maximum distance (km)', type: 'number', step: '0.1' },
  { key: 'installs_min', label: 'National installs minimum', type: 'number' },
  { key: 'installs_max', label: 'National installs maximum', type: 'number' },
  { key: 'district_installs_min', label: 'District installs minimum', type: 'number' },
  { key: 'district_installs_max', label: 'District installs maximum', type: 'number' },
  { key: 'oms_matched', label: 'App user', type: 'select', options: [['true', 'Yes'], ['false', 'No']] },
  { key: 'days_since_last_order', label: 'Days since last order', type: 'number' },
  { key: 'last_contact_after', label: 'Last contact from', type: 'date' },
  { key: 'last_contact_before', label: 'Last contact to', type: 'date' },
  { key: 'last_order_after', label: 'Last order from', type: 'date' },
  { key: 'last_order_before', label: 'Last order to', type: 'date' },
  { key: 'first_seen_after', label: 'PMSG first seen from', type: 'date' },
  { key: 'first_seen_before', label: 'PMSG first seen to', type: 'date' },
  { key: 'last_seen_after', label: 'PMSG last seen from', type: 'date' },
  { key: 'last_seen_before', label: 'PMSG last seen to', type: 'date' },
  { key: 'registered_after', label: 'Registered from', type: 'date' },
  { key: 'registered_before', label: 'Registered to', type: 'date' },
  { key: 'last_login_after', label: 'Last login from', type: 'date' },
  { key: 'last_login_before', label: 'Last login to', type: 'date' },
  { key: 'orders_30_count_min', label: '30-day order count minimum', type: 'number' },
  { key: 'orders_30_count_max', label: '30-day order count maximum', type: 'number' },
  { key: 'orders_30_value_min', label: '30-day order value minimum', type: 'number' },
  { key: 'orders_30_value_max', label: '30-day order value maximum', type: 'number' },
  { key: 'orders_90_count_min', label: '90-day order count minimum', type: 'number' },
  { key: 'orders_90_count_max', label: '90-day order count maximum', type: 'number' },
  { key: 'orders_90_value_min', label: '90-day order value minimum', type: 'number' },
  { key: 'orders_90_value_max', label: '90-day order value maximum', type: 'number' },
  { key: 'lifetime_value_min', label: 'Lifetime value minimum', type: 'number' },
  { key: 'lifetime_value_max', label: 'Lifetime value maximum', type: 'number' },
  { key: 'average_order_value_min', label: 'Average order value minimum', type: 'number' },
  { key: 'average_order_value_max', label: 'Average order value maximum', type: 'number' },
  { key: 'top_brand', label: 'Top brand', type: 'text' },
  { key: 'current_orders_min', label: 'Current orders minimum', type: 'number' },
  { key: 'current_orders_max', label: 'Current orders maximum', type: 'number' },
];

const VIEW_COLUMNS = [
  ['lead', 'Lead'],
  ['stage', 'Stage'],
  ['tags', 'Tags'],
  ['hub', 'Hub'],
  ['owner', 'Owner'],
  ['district', 'District'],
  ['address', 'Address'],
  ['installs', 'Installs'],
  ['orders', 'Orders (30 / 90d)'],
  ['last_order', 'Last order'],
  ['last_contact', 'Last contact'],
  ['app_activity', 'App activity'],
  ['follow_up', 'Follow-up'],
  ['actions', 'Actions'],
];

const VIEW_SORT_FIELDS = [
  ['', 'Default (recently updated)'],
  ['created_at', 'Created date'],
  ['updated_at', 'Last updated'],
  ['display_name', 'Lead name'],
  ['stage', 'Stage'],
  ['last_contact_date', 'Last contacted'],
  ['follow_up_date', 'Follow-up date'],
];

let filterRowId = 0;
const makeFilterRow = (field = '', value = '') => ({ id: ++filterRowId, field, value });
const filtersToRows = (filters = {}, fields = VIEW_FILTER_FIELDS) => Object.entries(filters)
  .filter(([field]) => fields.some((option) => option.key === field))
  .map(([field, value]) => {
    const definition = fields.find((option) => option.key === field);
    return makeFilterRow(field, definition?.type === 'list' && Array.isArray(value) ? value.join(', ') : value);
  });

const emptyView = {
  id: null,
  name: '',
  filters: [],
  sortField: '',
  sortDirection: 'asc',
  columns: [],
  roles: [],
  active: true,
};
const emptyVariable = { id: null, key: '', label: '', type: 'text', allowedText: '', active: true };
const emptyDisposition = { id: null, key: '', label: '', sortOrder: 0, isQuick: false, active: true };
const SMARTFLO_PAGE_SIZE = 10;
const emptySmartfloPagination = { page: 1, limit: SMARTFLO_PAGE_SIZE, total: 0, totalPages: 0 };

const readData = (response, key) => response?.data?.[key] || [];
const CrmSettings = () => {
  const { user } = useAuth();
  const canManageSmartflo = ['manager', 'superAdmin'].includes(user?.role);
  const [tab, setTab] = useState('views');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [views, setViews] = useState([]);
  const [variables, setVariables] = useState([]);
  const [dispositions, setDispositions] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [assignableStaff, setAssignableStaff] = useState([]);
  const [smartfloUsers, setSmartfloUsers] = useState([]);
  const [smartfloUserOptions, setSmartfloUserOptions] = useState([]);
  const [smartfloSearchInput, setSmartfloSearchInput] = useState('');
  const [smartfloSearch, setSmartfloSearch] = useState('');
  const [smartfloAssignment, setSmartfloAssignment] = useState('all');
  const [smartfloPage, setSmartfloPage] = useState(1);
  const [smartfloPagination, setSmartfloPagination] = useState(emptySmartfloPagination);
  const [smartfloListLoading, setSmartfloListLoading] = useState(false);
  const smartfloRequestId = useRef(0);
  const [smartfloCallerIds, setSmartfloCallerIds] = useState([]);
  const [smartfloRoles, setSmartfloRoles] = useState([]);
  const [smartfloLoadError, setSmartfloLoadError] = useState('');
  const [callerIdLoadError, setCallerIdLoadError] = useState('');
  const [roleLoadError, setRoleLoadError] = useState('');
  const [agentForm, setAgentForm] = useState({
    omsUserId: '',
    phone: '',
    smartfloRoleId: '',
    callerId: '',
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [viewForm, setViewForm] = useState(emptyView);
  const [variableForm, setVariableForm] = useState(emptyVariable);
  const [dispositionForm, setDispositionForm] = useState(emptyDisposition);
  const viewFilterFields = useMemo(() => [
    ...VIEW_FILTER_FIELDS,
    ...variables.filter((variable) => variable.active).map((variable) => ({
      key: `variable_${variable.key}`,
      label: `Variable · ${variable.label}`,
      type: variable.type === 'list' ? 'select' : variable.type,
      options: variable.type === 'list'
        ? (variable.allowedValues || []).map((value) => [value, value])
        : undefined,
    })),
  ], [variables]);
  const viewColumns = useMemo(() => [
    ...VIEW_COLUMNS,
    ...variables.filter((variable) => variable.active).map((variable) => [
      `variable_${variable.key}`,
      `Variable · ${variable.label}`,
    ]),
  ], [variables]);

  const notify = (type, text) => setMessage({ type, text });

  const loadSmartfloUsers = useCallback(async () => {
    if (!canManageSmartflo) return;
    const requestId = smartfloRequestId.current + 1;
    smartfloRequestId.current = requestId;
    setSmartfloListLoading(true);
    setSmartfloLoadError('');
    try {
      const response = await getCrmSettingsSmartfloAgents({
        page: smartfloPage,
        limit: SMARTFLO_PAGE_SIZE,
        search: smartfloSearch,
        assignment: smartfloAssignment,
      });
      if (requestId !== smartfloRequestId.current) return;
      const result = response?.data || {};
      const returnedUsers = result.users || [];
      const searchTerm = smartfloSearch.toLowerCase();
      const phoneSearch = /^[+\d\s().-]+$/.test(searchTerm) ? searchTerm.replace(/\D/g, '') : '';
      const phoneNeedle = phoneSearch.length > 10 ? phoneSearch.slice(-10) : phoneSearch;
      const fallbackUsers = result.pagination ? returnedUsers : returnedUsers.filter((row) => {
        const matchesAssignment = smartfloAssignment === 'all' ||
          (smartfloAssignment === 'assigned' ? row.smartfloRegistered : !row.smartfloRegistered);
        const matchesSearch = !searchTerm || [row.name, row.email, row.registeredMobile]
          .some((value) => String(value || '').toLowerCase().includes(searchTerm)) ||
          (phoneNeedle && String(row.registeredMobile || '').replace(/\D/g, '').includes(phoneNeedle));
        return matchesAssignment && matchesSearch;
      });
      const fallbackTotalPages = Math.ceil(fallbackUsers.length / SMARTFLO_PAGE_SIZE);
      const fallbackPage = Math.min(smartfloPage, fallbackTotalPages || 1);
      const pagination = result.pagination || {
        page: fallbackPage,
        limit: SMARTFLO_PAGE_SIZE,
        total: fallbackUsers.length,
        totalPages: fallbackTotalPages,
      };
      const visibleUsers = result.pagination
        ? returnedUsers
        : fallbackUsers.slice((fallbackPage - 1) * SMARTFLO_PAGE_SIZE, fallbackPage * SMARTFLO_PAGE_SIZE);
      setSmartfloUsers(visibleUsers);
      setSmartfloUserOptions(result.selectableUsers || returnedUsers.filter((row) => !row.smartfloRegistered));
      setSmartfloPagination(pagination);
      if (pagination.page && pagination.page !== smartfloPage) {
        setSmartfloPage(pagination.page);
      }
    } catch (error) {
      if (requestId !== smartfloRequestId.current) return;
      setSmartfloLoadError(error.message || 'Could not load Smartflo agents.');
      setSmartfloUsers([]);
      setSmartfloUserOptions([]);
      setSmartfloPagination(emptySmartfloPagination);
    } finally {
      if (requestId === smartfloRequestId.current) setSmartfloListLoading(false);
    }
  }, [canManageSmartflo, smartfloAssignment, smartfloPage, smartfloSearch]);

  const loadAll = async () => {
    setLoading(true);
    setMessage(null);
    setSmartfloLoadError('');
    setCallerIdLoadError('');
    setRoleLoadError('');
    try {
      const [viewResponse, variableResponse, dispositionResponse, hubResponse, staffResponse, callerIdResponse, roleResponse] = await Promise.all([
        getCrmSettingsViews(),
        getCrmSettingsVariables(),
        getCrmSettingsDispositions(),
        getCrmSettingsHubNumbers(),
        searchCrmAssignableStaff(),
        canManageSmartflo ? getCrmSettingsSmartfloCallerIds().catch(() => {
          setCallerIdLoadError('Unable to load Smartflo Caller IDs');
          return null;
        }) : Promise.resolve(null),
        canManageSmartflo ? getCrmSettingsSmartfloRoles().catch(() => {
          setRoleLoadError('Unable to load Smartflo roles');
          return null;
        }) : Promise.resolve(null),
      ]);
      setViews(readData(viewResponse, 'views'));
      setVariables(readData(variableResponse, 'variables'));
      setDispositions(readData(dispositionResponse, 'dispositions'));
      setHubs(readData(hubResponse, 'hubs'));
      setAssignableStaff(readData(staffResponse, 'users'));
      setSmartfloCallerIds(callerIdResponse ? readData(callerIdResponse, 'callerIds') : []);
      setSmartfloRoles(roleResponse ? readData(roleResponse, 'roles') : []);
    } catch (error) {
      notify('error', error.message || 'Could not load CRM settings.');
    } finally {
      setLoading(false);
    }
  };

  // Settings are loaded once when the protected admin page opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSmartfloPage(1);
      setSmartfloSearch(smartfloSearchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [smartfloSearchInput]);

  useEffect(() => { loadSmartfloUsers(); }, [loadSmartfloUsers]);

  useEffect(() => {
    const closeDropdown = (event) => {
      if (!event.target.closest('.crm-checkbox-dropdown')) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', closeDropdown);
    return () => document.removeEventListener('mousedown', closeDropdown);
  }, []);

  const runSave = async (action, successText) => {
    setSaving(true);
    setMessage(null);
    try {
      await action();
      await loadAll();
      notify('success', successText);
      return true;
    } catch (error) {
      if (error.details?.partialSuccess) await loadAll();
      notify('error', error.message || 'Could not save this setting.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveView = async (event) => {
    event.preventDefault();
    const filters = viewForm.filters.reduce((result, filter) => {
      if (filter.field && filter.value !== '' && filter.value != null && (!Array.isArray(filter.value) || filter.value.length > 0)) {
        const definition = viewFilterFields.find((option) => option.key === filter.field);
        if (definition?.type === 'list') {
          result[filter.field] = String(filter.value).split(',').map((value) => value.trim()).filter(Boolean);
        } else if (definition?.type === 'boolean') {
          result[filter.field] = filter.value === true || filter.value === 'true';
        } else {
          result[filter.field] = filter.value;
        }
      }
      return result;
    }, {});
    const payload = {
      name: viewForm.name,
      filters,
      sort: viewForm.sortField
        ? { field: viewForm.sortField.trim(), direction: viewForm.sortDirection }
        : {},
      columns: viewForm.columns,
      roles: viewForm.roles,
      active: viewForm.active,
    };
    const ok = await runSave(
      () => viewForm.id ? updateCrmView(viewForm.id, payload) : createCrmView(payload),
      viewForm.id ? 'View updated.' : 'View created.'
    );
    if (ok) setViewForm(emptyView);
  };

  const editView = (view) => setViewForm({
    id: view.id,
    name: view.name,
    filters: filtersToRows(view.filters, viewFilterFields),
    sortField: view.sort?.field || '',
    sortDirection: view.sort?.direction || 'asc',
    columns: (view.columns || []).filter((column) => viewColumns.some(([value]) => value === column)),
    roles: (view.roles || []).filter((role) => ROLE_OPTIONS.some(([value]) => value === role)),
    active: view.active,
  });

  const saveVariable = async (event) => {
    event.preventDefault();
    const allowedValues = variableForm.allowedText.split(',').map((item) => item.trim()).filter(Boolean);
    const payload = variableForm.id
      ? {
          label: variableForm.label,
          active: variableForm.active,
          ...(variableForm.type === 'list' ? { allowedValues } : {}),
        }
      : {
          key: variableForm.key,
          label: variableForm.label,
          type: variableForm.type,
          allowedValues: variableForm.type === 'list' ? allowedValues : [],
          active: variableForm.active,
        };
    const ok = await runSave(
      () => variableForm.id
        ? updateCrmVariable(variableForm.id, payload)
        : createCrmVariable(payload),
      variableForm.id ? 'Variable updated.' : 'Variable created.'
    );
    if (ok) setVariableForm(emptyVariable);
  };

  const editVariable = (variable) => setVariableForm({
    id: variable.id,
    key: variable.key,
    label: variable.label,
    type: variable.type,
    allowedText: (variable.allowedValues || []).join(', '),
    active: variable.active,
  });

  const saveDisposition = async (event) => {
    event.preventDefault();
    const payload = dispositionForm.id
      ? {
          label: dispositionForm.label,
          sortOrder: Number(dispositionForm.sortOrder),
          isQuick: dispositionForm.isQuick,
          active: dispositionForm.active,
        }
      : {
          key: dispositionForm.key,
          label: dispositionForm.label,
          sortOrder: Number(dispositionForm.sortOrder),
          isQuick: dispositionForm.isQuick,
          active: dispositionForm.active,
        };
    const ok = await runSave(
      () => dispositionForm.id
        ? updateCrmDisposition(dispositionForm.id, payload)
        : createCrmDisposition(payload),
      dispositionForm.id ? 'Disposition updated.' : 'Disposition created.'
    );
    if (ok) setDispositionForm(emptyDisposition);
  };

  const editDisposition = (row) => setDispositionForm({
    id: row.id,
    key: row.key,
    label: row.label,
    sortOrder: row.sortOrder,
    isQuick: row.isQuick,
    active: row.active,
  });

  const updateHubDraft = (hubId, field, value) => {
    setHubs((rows) => rows.map((hub) => hub.hubId === hubId ? { ...hub, [field]: value } : hub));
  };

  const saveHub = (hub) => runSave(
    () => saveCrmHubNumbers(hub.hubId, {
      whatsappNumber: hub.whatsappNumber,
      voiceVirtualNumber: hub.voiceVirtualNumber,
    }),
    `${hub.name} numbers saved.`
  );

  const selectAgentUser = (row) => setAgentForm((current) => ({
    ...current,
    omsUserId: String(row.id),
    phone: row.registeredMobile || '',
  }));

  const saveSmartfloAgent = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await createCrmSmartfloAgent({
        oms_user_id: Number(agentForm.omsUserId),
        phone: agentForm.phone,
        caller_id: agentForm.callerId,
        smartflo_role_id: agentForm.smartfloRoleId,
      });
      setAgentForm({ omsUserId: '', phone: '', smartfloRoleId: '', callerId: '' });
      await loadSmartfloUsers();
      notify('success', 'Smartflo agent created.');
    } catch (error) {
      if (error.details?.partialSuccess) await loadSmartfloUsers();
      notify('error', error.message || 'Could not create the Smartflo agent.');
    } finally {
      setSaving(false);
    }
  };

  const removeSmartfloAgent = async (row) => {
    if (!row.smartfloRegistered || !row.smartfloUserId) return;
    if (!window.confirm(
      `Delete ${row.name} from Smartflo? The OMS user will remain available.`
    )) return;

    setSaving(true);
    setMessage(null);
    try {
      await deleteCrmSmartfloAgent(row.smartfloUserId);
      await loadSmartfloUsers();
      notify('success', 'Smartflo agent deleted.');
    } catch (error) {
      if (error.details?.partialSuccess) {
        await loadSmartfloUsers();
      }
      notify('error', error.message || 'Could not delete the Smartflo agent.');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => setViewForm((current) => ({
    ...current,
    roles: current.roles.includes(role)
      ? current.roles.filter((item) => item !== role)
      : [...current.roles, role],
  }));

  const addViewFilter = () => setViewForm((current) => ({
    ...current,
    filters: [...current.filters, makeFilterRow()],
  }));

  const updateViewFilter = (id, changes) => setViewForm((current) => ({
    ...current,
    filters: current.filters.map((filter) => filter.id === id ? { ...filter, ...changes } : filter),
  }));

  const removeViewFilter = (id) => setViewForm((current) => ({
    ...current,
    filters: current.filters.filter((filter) => filter.id !== id),
  }));

  const toggleViewColumn = (column) => setViewForm((current) => ({
    ...current,
    columns: current.columns.includes(column)
      ? current.columns.filter((item) => item !== column)
      : [...current.columns, column],
  }));

  const deactivate = async (kind, id) => {
    const actions = {
      view: () => deactivateCrmView(id),
      variable: () => deactivateCrmVariable(id),
      disposition: () => deactivateCrmDisposition(id),
    };
    await runSave(actions[kind], `${kind[0].toUpperCase()}${kind.slice(1)} deactivated.`);
  };

  return (
    <main className="container crm-settings-page">
      <div className="crm-settings-header">
        <div>
          <h2 className="page-title">CRM settings</h2>
          <p>Configure shared CRM views, fields, call outcomes and Smartflo agents.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={() => { loadAll(); loadSmartfloUsers(); }} disabled={loading || smartfloListLoading}>Refresh</button>
      </div>

      <div className="crm-settings-tabs" role="tablist">
        {[...TABS, ...(canManageSmartflo ? [['smartfloAgents', 'Smartflo Agents']] : [])].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={tab === value ? 'active' : ''}
            onClick={() => { setTab(value); setMessage(null); setOpenDropdown(null); }}
          >
            {label}
          </button>
        ))}
      </div>

      {message && <div className={`crm-settings-message ${message.type}`}>{message.text}</div>}
      {loading ? <div className="card crm-settings-loading">Loading CRM settings…</div> : (
        <>
          {tab === 'views' && (
            <section className="crm-settings-grid crm-settings-standard-grid">
              <form className="card crm-settings-form" onSubmit={saveView}>
                <div className="crm-settings-section-heading"><div className="crm-settings-card-title">{viewForm.id ? 'Edit view' : 'Create view'}</div><p className="crm-settings-help">Define the filters, columns and roles for a reusable lead view.</p></div>
                <label>Name<input className="form-control" value={viewForm.name} onChange={(e) => setViewForm({ ...viewForm, name: e.target.value })} required /></label>
                <div className="crm-view-filter-editor">
                  <div className="crm-view-field-heading">
                    <span>Filters</span>
                    <button className="btn btn-secondary crm-btn-xs" type="button" onClick={addViewFilter}>+ Add filter</button>
                  </div>
                  {viewForm.filters.length === 0 ? (
                    <div className="crm-view-empty-selection">No filters added. This view will show all Leads.</div>
                  ) : viewForm.filters.map((filter) => (
                    <ViewFilterRow
                      key={filter.id}
                      filter={filter}
                      hubs={hubs}
                      staff={assignableStaff}
                      fields={viewFilterFields}
                      selectedFields={viewForm.filters.map((item) => item.field)}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                      onChange={(changes) => updateViewFilter(filter.id, changes)}
                      onRemove={() => removeViewFilter(filter.id)}
                    />
                  ))}
                </div>
                <div className="crm-settings-two-col">
                  <label>Sort field<select className="form-control" value={viewForm.sortField} onChange={(e) => setViewForm({ ...viewForm, sortField: e.target.value })}>{VIEW_SORT_FIELDS.map(([value, label]) => <option key={value || 'default'} value={value}>{label}</option>)}</select></label>
                  <label>Direction<select className="form-control" value={viewForm.sortDirection} onChange={(e) => setViewForm({ ...viewForm, sortDirection: e.target.value })}><option value="asc">Ascending</option><option value="desc">Descending</option></select></label>
                </div>
                <div className="crm-view-columns-field">
                  <span>Columns</span>
                  <CheckboxDropdown
                    options={viewColumns}
                    selected={viewForm.columns}
                    onToggle={toggleViewColumn}
                    emptyLabel="Select columns"
                    open={openDropdown === 'columns'}
                    onOpenChange={(open) => setOpenDropdown(open ? 'columns' : null)}
                  />
                </div>
                <fieldset className="crm-settings-role-list"><legend>Visible to roles</legend>{ROLE_OPTIONS.map(([value, label]) => <label key={value}><input type="checkbox" checked={viewForm.roles.includes(value)} onChange={() => toggleRole(value)} />{label}</label>)}</fieldset>
                <label className="crm-settings-active"><input type="checkbox" checked={viewForm.active} onChange={(e) => setViewForm({ ...viewForm, active: e.target.checked })} />Active</label>
                <div className="crm-settings-form-actions"><button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save view'}</button>{viewForm.id && <button className="btn btn-secondary" type="button" onClick={() => setViewForm(emptyView)}>Cancel</button>}</div>
              </form>
              <SettingsList title="Configured views" subtitle="Saved layouts available across CRM." count={views.length} empty="No CRM views configured.">{views.map((view) => <SettingsRow key={view.id} title={view.name} subtitle={`${(view.columns || []).length} columns · ${(view.roles || []).join(', ') || 'No roles'}`} active={view.active} onEdit={() => editView(view)} onDeactivate={() => deactivate('view', view.id)} />)}</SettingsList>
            </section>
          )}

          {tab === 'variables' && (
            <section className="crm-settings-grid crm-settings-standard-grid">
              <form className="card crm-settings-form" onSubmit={saveVariable}>
                <div className="crm-settings-section-heading"><div className="crm-settings-card-title">{variableForm.id ? 'Edit variable' : 'Create variable'}</div><p className="crm-settings-help">Add a custom field for information your CRM team needs to track.</p></div>
                <div className="crm-settings-two-col"><label>Key<input className="form-control" value={variableForm.key} disabled={!!variableForm.id} onChange={(e) => setVariableForm({ ...variableForm, key: e.target.value })} required /></label><label>Type<select className="form-control" value={variableForm.type} disabled={!!variableForm.id} onChange={(e) => setVariableForm({ ...variableForm, type: e.target.value })}><option value="text">Text</option><option value="number">Number</option><option value="list">List</option><option value="date">Date</option></select></label></div>
                <label>Name<input className="form-control" value={variableForm.label} onChange={(e) => setVariableForm({ ...variableForm, label: e.target.value })} required /></label>
                {variableForm.type === 'list' && <label>Allowed values <span>(comma separated)</span><textarea className="form-control" rows={3} value={variableForm.allowedText} onChange={(e) => setVariableForm({ ...variableForm, allowedText: e.target.value })} required /></label>}
                <label className="crm-settings-active"><input type="checkbox" checked={variableForm.active} onChange={(e) => setVariableForm({ ...variableForm, active: e.target.checked })} />Active</label>
                <div className="crm-settings-form-actions"><button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save variable'}</button>{variableForm.id && <button className="btn btn-secondary" type="button" onClick={() => setVariableForm(emptyVariable)}>Cancel</button>}</div>
              </form>
              <SettingsList title="Configured variables" subtitle="Custom fields available on CRM leads." count={variables.length} empty="No variables configured.">{variables.map((variable) => <SettingsRow key={variable.id} title={variable.label} subtitle={`${variable.key} · ${variable.type}${variable.type === 'list' ? ` · ${(variable.allowedValues || []).length} values` : ''}`} active={variable.active} onEdit={() => editVariable(variable)} onDeactivate={() => deactivate('variable', variable.id)} />)}</SettingsList>
            </section>
          )}

          {tab === 'dispositions' && (
            <section className="crm-settings-grid crm-settings-standard-grid">
              <form className="card crm-settings-form" onSubmit={saveDisposition}>
                <div className="crm-settings-section-heading"><div className="crm-settings-card-title">{dispositionForm.id ? 'Edit disposition' : 'Create disposition'}</div><p className="crm-settings-help">Configure the outcomes agents can select after a call.</p></div>
                <div className="crm-settings-two-col"><label>Key<input className="form-control" value={dispositionForm.key} disabled={!!dispositionForm.id} onChange={(e) => setDispositionForm({ ...dispositionForm, key: e.target.value })} required /></label><label>Sort order<input className="form-control" type="number" min="0" max="10000" value={dispositionForm.sortOrder} onChange={(e) => setDispositionForm({ ...dispositionForm, sortOrder: e.target.value })} /></label></div>
                <label>Name<input className="form-control" value={dispositionForm.label} onChange={(e) => setDispositionForm({ ...dispositionForm, label: e.target.value })} required /></label>
                <label className="crm-settings-active"><input type="checkbox" checked={dispositionForm.isQuick} onChange={(e) => setDispositionForm({ ...dispositionForm, isQuick: e.target.checked })} />Show as a quick action in Calling View</label>
                <label className="crm-settings-active"><input type="checkbox" checked={dispositionForm.active} onChange={(e) => setDispositionForm({ ...dispositionForm, active: e.target.checked })} />Active</label>
                <div className="crm-settings-form-actions"><button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save disposition'}</button>{dispositionForm.id && <button className="btn btn-secondary" type="button" onClick={() => setDispositionForm(emptyDisposition)}>Cancel</button>}</div>
              </form>
              <SettingsList title="Configured dispositions" subtitle="Call outcomes available to CRM agents." count={dispositions.length} empty="No dispositions configured.">{dispositions.map((row) => <SettingsRow key={row.id} title={row.label} subtitle={`${row.key} · order ${row.sortOrder}${row.isQuick ? ' · quick action' : ''}`} active={row.active} onEdit={() => editDisposition(row)} onDeactivate={() => deactivate('disposition', row.id)} />)}</SettingsList>
            </section>
          )}

          {tab === 'hubNumbers' && (
            <section className="card crm-hub-number-list">
              <div className="crm-settings-card-title">Hub communication numbers</div>
              <p className="crm-settings-help">Use country code where required, for example +919876543210.</p>
              {hubs.length === 0 ? <div className="crm-settings-empty">No active Hubs found.</div> : hubs.map((hub) => (
                <div className="crm-hub-number-row" key={hub.hubId}>
                  <div className="crm-hub-number-name"><strong>{hub.name}</strong><span>{hub.city || '—'} · {hub.hubType || 'Hub'}</span></div>
                  <label>WhatsApp number<input className="form-control" value={hub.whatsappNumber || ''} onChange={(e) => updateHubDraft(hub.hubId, 'whatsappNumber', e.target.value)} placeholder="+919876543210" /></label>
                  <label>Voice virtual number<input className="form-control" value={hub.voiceVirtualNumber || ''} onChange={(e) => updateHubDraft(hub.hubId, 'voiceVirtualNumber', e.target.value)} placeholder="+919876543210" /></label>
                  <button className="btn btn-primary" type="button" disabled={saving} onClick={() => saveHub(hub)}>Save</button>
                </div>
              ))}
            </section>
          )}

          {tab === 'smartfloAgents' && canManageSmartflo && (
            <section className="crm-settings-grid crm-smartflo-settings">
              {smartfloLoadError && <div className="crm-settings-message error">{smartfloLoadError}</div>}
              <form className="card crm-settings-form crm-smartflo-form" onSubmit={saveSmartfloAgent}>
                <div className="crm-smartflo-card-heading">
                  <div className="crm-settings-card-title">Create Smartflo agent</div>
                  <p className="crm-settings-help">Connect an eligible CRM user using a Smartflo role and caller ID.</p>
                </div>
                <div className="crm-smartflo-form-fields">
                  <label className="crm-smartflo-field-wide">CRM user
                    <select className="form-control" value={agentForm.omsUserId} onChange={(event) => {
                      const row = smartfloUserOptions.find((item) => item.id === Number(event.target.value));
                      setAgentForm({ ...agentForm, omsUserId: event.target.value, phone: row?.registeredMobile || '' });
                    }} required>
                      <option value="">Select CRM user</option>
                      {smartfloUserOptions.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.email}</option>)}
                    </select>
                  </label>
                  <label>Mobile number<input className="form-control" inputMode="tel" value={agentForm.phone} onChange={(event) => setAgentForm({ ...agentForm, phone: event.target.value })} placeholder="9876543210" required /></label>
                  <label>Smartflo role
                    <select className="form-control" value={agentForm.smartfloRoleId} onChange={(event) => setAgentForm({ ...agentForm, smartfloRoleId: event.target.value })} disabled={!!roleLoadError || smartfloRoles.length === 0} required>
                      <option value="">{smartfloRoles.length === 0 ? 'No Smartflo roles available' : 'Select Smartflo role'}</option>
                      {smartfloRoles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
                    </select>
                  </label>
                  <label className="crm-smartflo-field-wide">Smartflo Caller ID / DID
                    <select className="form-control" value={agentForm.callerId} onChange={(event) => setAgentForm({ ...agentForm, callerId: event.target.value })} disabled={!!callerIdLoadError || smartfloCallerIds.length === 0} required>
                      <option value="">{smartfloCallerIds.length === 0 ? 'No Smartflo Caller IDs available' : 'Select Smartflo Caller ID'}</option>
                      {smartfloCallerIds.slice(0, 1).map((callerId) => <option key={callerId.id} value={callerId.id}>{callerId.did}{callerId.name ? ` · ${callerId.name}` : ''}</option>)}
                    </select>
                  </label>
                </div>
                {roleLoadError && <div className="crm-settings-message error">{roleLoadError}</div>}
                {callerIdLoadError && <div className="crm-settings-message error">{callerIdLoadError}</div>}
                {!callerIdLoadError && smartfloCallerIds.length === 0 && <div className="crm-settings-help">Smartflo returned no available Caller IDs.</div>}
                <div className="crm-settings-form-actions"><button className="btn btn-primary" disabled={saving || !agentForm.smartfloRoleId || !agentForm.callerId}>{saving ? 'Creating…' : 'Create Agent'}</button></div>
              </form>
              <div className="card crm-smartflo-user-list">
                <div className="crm-smartflo-list-heading">
                  <div><div className="crm-settings-card-title">Eligible CRM users</div><p className="crm-settings-help">CRM staff and managers available for Smartflo registration.</p></div>
                  <span className="crm-smartflo-user-count">{smartfloPagination.total}</span>
                </div>
                <div className="crm-smartflo-user-tools">
                  <label className="crm-smartflo-search">
                    <span>Search users</span>
                    <input
                      className="form-control"
                      type="search"
                      value={smartfloSearchInput}
                      onChange={(event) => setSmartfloSearchInput(event.target.value)}
                      placeholder="Search name, email or phone"
                    />
                  </label>
                  <label className="crm-smartflo-assignment-filter">
                    <span>Agent status</span>
                    <select className="form-control" value={smartfloAssignment} onChange={(event) => {
                      setSmartfloAssignment(event.target.value);
                      setSmartfloPage(1);
                    }}>
                      <option value="all">All users</option>
                      <option value="assigned">Assigned agents</option>
                      <option value="unassigned">Not assigned</option>
                    </select>
                  </label>
                </div>
                {smartfloListLoading && smartfloUsers.length === 0 ? (
                  <div className="crm-settings-empty">Loading eligible users…</div>
                ) : smartfloUsers.length === 0 ? (
                  <div className="crm-settings-empty">No users match this search and agent status.</div>
                ) : (
                  <>
                    <div className="crm-smartflo-user-header"><span>User</span><span>Role</span><span>Mobile</span><span>Status</span><span /></div>
                    {smartfloUsers.map((row) => (
                      <div className="crm-smartflo-user-row" key={row.id}>
                        <div className="crm-smartflo-user-identity"><strong>{row.name}</strong><span title={row.email}>{row.email}</span></div>
                        <span className="crm-smartflo-role">{row.role}</span>
                        <span className="crm-smartflo-mobile">{row.registeredMobile || 'No mobile'}</span>
                        <div className="crm-smartflo-state">
                          <span className={row.smartfloRegistered ? 'crm-smartflo-status registered' : 'crm-smartflo-status'}>
                            {!row.smartfloRegistered
                              ? 'Not registered'
                              : row.extensionEnabled
                                ? 'Mobile + Extension'
                                : 'Mobile only'}
                          </span>
                          {row.extensionEnabled ? (
                            <span className="crm-smartflo-extension-value">Extension configured</span>
                          ) : (
                            <>
                              <span className="crm-smartflo-extension-value warning">Extension not configured in Smartflo</span>
                              <span className="crm-smartflo-extension-instruction">Enable Agent Extension in Smartflo and click Refresh.</span>
                            </>
                          )}
                        </div>
                        {!row.smartfloRegistered ? (
                          <button className="btn btn-secondary crm-btn-xs" type="button" disabled={saving} onClick={() => selectAgentUser(row)}>Create Agent</button>
                        ) : (
                          <button className="btn btn-danger crm-btn-xs" type="button" disabled={saving || !row.smartfloUserId} onClick={() => removeSmartfloAgent(row)}>Delete Agent</button>
                        )}
                      </div>
                    ))}
                  </>
                )}
                {smartfloPagination.total > 0 && (
                  <div className="crm-smartflo-pagination">
                    <span>
                      Showing {(smartfloPagination.page - 1) * smartfloPagination.limit + 1}–{Math.min(smartfloPagination.page * smartfloPagination.limit, smartfloPagination.total)} of {smartfloPagination.total}
                    </span>
                    <div>
                      <button className="btn btn-secondary crm-btn-xs" type="button" disabled={smartfloListLoading || smartfloPagination.page <= 1} onClick={() => setSmartfloPage((page) => Math.max(1, page - 1))}>Previous</button>
                      <span>Page {smartfloPagination.page} of {smartfloPagination.totalPages || 1}</span>
                      <button className="btn btn-secondary crm-btn-xs" type="button" disabled={smartfloListLoading || smartfloPagination.page >= smartfloPagination.totalPages} onClick={() => setSmartfloPage((page) => page + 1)}>Next</button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
};

const CheckboxDropdown = ({ options, selected, onToggle, emptyLabel, open, onOpenChange }) => {
  const selectedLabels = options
    .filter(([value]) => selected.includes(value))
    .map(([, label]) => label);
  const summary = selectedLabels.length === 0
    ? emptyLabel
    : selectedLabels.length <= 2
      ? selectedLabels.join(', ')
      : `${selectedLabels.length} selected`;

  return (
    <details className="crm-checkbox-dropdown" open={open}>
      <summary onClick={(event) => { event.preventDefault(); onOpenChange(!open); }}>{summary}</summary>
      <div className="crm-checkbox-dropdown-menu">
        {options.map(([value, label]) => (
          <label key={value}>
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={() => onToggle(value)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </details>
  );
};

const ViewFilterValue = ({ definition, value, hubs, staff, open, onOpenChange, onChange }) => {
  if (!definition) {
    return <input className="form-control" disabled placeholder="Select a field first" />;
  }
  if (definition.type === 'multi-select') {
    return (
      <CheckboxDropdown
        options={definition.options}
        selected={Array.isArray(value) ? value : []}
        onToggle={(option) => {
          const current = Array.isArray(value) ? value : [];
          onChange(current.includes(option)
            ? current.filter((item) => item !== option)
            : [...current, option]);
        }}
        emptyLabel="Select values"
        open={open}
        onOpenChange={onOpenChange}
      />
    );
  }
  if (definition.type === 'select') {
    return (
      <select className="form-control" value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select value</option>
        {definition.options.map(([optionValue, label]) => <option key={optionValue} value={optionValue}>{label}</option>)}
      </select>
    );
  }
  if (definition.type === 'boolean') {
    return (
      <select className="form-control" value={String(value)} onChange={(event) => onChange(event.target.value === 'true')}>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (definition.type === 'hub') {
    return (
      <select className="form-control" value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select Hub</option>
        {hubs.map((hub) => <option key={hub.hubId} value={hub.hubId}>{hub.name}{hub.city ? ` · ${hub.city}` : ''}</option>)}
      </select>
    );
  }
  if (definition.type === 'staff') {
    const hasSelectedUser = staff.some((user) => String(user.id) === String(value));
    return (
      <select className="form-control" value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select user</option>
        {value && !hasSelectedUser && <option value={value}>Previously selected user</option>}
        {staff.map((user) => (
          <option key={user.id} value={user.id}>
            {user.name || user.email}{user.name && user.email ? ` · ${user.email}` : ''}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      className="form-control"
      type={definition.type === 'list' ? 'text' : definition.type}
      min={definition.type === 'number' ? '0' : undefined}
      step={definition.step}
      value={value ?? ''}
      placeholder={definition.placeholder || 'Enter value'}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

const ViewFilterRow = ({ filter, hubs, staff, fields, selectedFields, openDropdown, setOpenDropdown, onChange, onRemove }) => {
  const definition = fields.find((option) => option.key === filter.field);
  return (
    <div className="crm-view-filter-row">
      <select
        className="form-control"
        aria-label="Filter field"
        value={filter.field}
        onChange={(event) => {
          const nextDefinition = fields.find((option) => option.key === event.target.value);
          const initialValue = nextDefinition?.type === 'multi-select' ? [] : nextDefinition?.type === 'boolean' ? true : '';
          onChange({ field: event.target.value, value: initialValue });
        }}
      >
        <option value="">Select field</option>
        {fields.map((option) => (
          <option
            key={option.key}
            value={option.key}
            disabled={option.key !== filter.field && selectedFields.includes(option.key)}
          >
            {option.label}
          </option>
        ))}
      </select>
      <ViewFilterValue
        definition={definition}
        value={filter.value}
        hubs={hubs}
        staff={staff}
        open={openDropdown === `filter-${filter.id}`}
        onOpenChange={(open) => setOpenDropdown(open ? `filter-${filter.id}` : null)}
        onChange={(value) => onChange({ value })}
      />
      <button className="crm-view-filter-remove" type="button" onClick={onRemove} aria-label="Remove filter">Remove</button>
    </div>
  );
};

const SettingsList = ({ children, empty, title, subtitle, count }) => (
  <div className="card crm-settings-list">
    <div className="crm-settings-list-heading">
      <div><div className="crm-settings-card-title">{title}</div>{subtitle && <p className="crm-settings-help">{subtitle}</p>}</div>
      <span className="crm-settings-count">{count}</span>
    </div>
    <div className="crm-settings-list-body">{React.Children.count(children) ? children : <div className="crm-settings-empty">{empty}</div>}</div>
  </div>
);

const SettingsRow = ({ title, subtitle, active, onEdit, onDeactivate }) => (
  <div className="crm-settings-row">
    <div><div className="crm-settings-row-title">{title}<span className={active ? 'active' : 'inactive'}>{active ? 'Active' : 'Inactive'}</span></div><div className="crm-settings-row-subtitle">{subtitle}</div></div>
    <div className="crm-settings-row-actions"><button type="button" className="btn btn-secondary crm-btn-xs" onClick={onEdit}>Edit</button>{active && <button type="button" className="btn crm-btn-xs" onClick={onDeactivate}>Deactivate</button>}</div>
  </div>
);

export default CrmSettings;
